import React, { useState, useEffect } from "react";
import { Play, X, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import VideoBrandFrame from "@/components/VideoBrandFrame";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Premium editorial video section — featured + optional secondary cards.
// Each video item: { id, title (i18n), desc (i18n), duration, poster, embed?: youtubeId|vimeoId|url }
// If no `embed` is present, clicking opens a graceful "coming soon" modal.
const VideoSection = ({
  overline,
  title,
  intro,
  featured,
  secondary = [],
  tone = "light", // "light" | "dark"
  testid = "video-section",
}) => {
  const { lang } = useLanguage();
  const [active, setActive] = useState(null);

  // Lock body scroll while modal is open + Esc handling
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [active]);

  if (!featured) return null;

  const isDark = tone === "dark";
  const wrapBg = isDark ? "bg-[#1A1513] text-[#FDFBF7]" : "bg-[#FDFBF7] text-[#2C2621]";
  const cardBg = isDark ? "bg-[#FDFBF7]/[0.04] border-[#FDFBF7]/15 hover:border-[#D4A373]/40" : "bg-white border-[#E8DFD2] hover:border-[#C16542]/40";
  const overlineTone = isDark ? "!text-[#D4A373]" : "";
  const introTone = isDark ? "text-[#FDFBF7]/75" : "text-[#5C5248]";
  const cardTitleTone = isDark ? "text-[#FDFBF7]" : "text-[#2C2621]";
  const cardDescTone = isDark ? "text-[#FDFBF7]/70" : "text-[#5C5248]";

  const items = [featured, ...secondary];

  return (
    <section
      className={`py-20 md:py-28 ${wrapBg}`}
      data-testid={testid}
      data-no-x-mark="true"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
          <div className="md:col-span-7">
            {overline && (
              <p className={`overline ${overlineTone}`} data-testid={`${testid}-overline`}>
                {pick(overline, lang)}
              </p>
            )}
            {title && (
              <h2
                className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
                data-testid={`${testid}-title`}
              >
                {pick(title, lang)}
              </h2>
            )}
          </div>
          {intro && (
            <div className="md:col-span-5">
              <p className={`text-base leading-relaxed ${introTone}`}>
                {pick(intro, lang)}
              </p>
            </div>
          )}
        </div>

        {/* Featured video — same hybrid layout as the secondaries:
            stacked on mobile (image + caption block), overlay on desktop
            (caption absolutely positioned above the image). */}
        <button
          type="button"
          onClick={() => setActive(featured)}
          className="group relative w-full flex flex-col md:block md:aspect-[16/9] overflow-hidden rounded-3xl text-left"
          data-testid={`${testid}-featured`}
          data-cms-scope={`${testid}.video.${featured.id}`}
        >
          <div className="relative w-full aspect-[16/9] md:aspect-auto md:absolute md:inset-0">
            <img
              src={featured.poster}
              alt={pick(featured.title, lang)}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" decoding="async"
              data-cms-role="poster"
              data-no-xaluca-logo="true"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/35 to-transparent" />

            {/* Play button */}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#FDFBF7]/95 text-[#C16542] flex items-center justify-center group-hover:bg-[#C16542] group-hover:text-[#FDFBF7] transition-colors shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
                <Play className="w-7 h-7 md:w-8 md:h-8 ml-1" strokeWidth={1.5} fill="currentColor" />
              </span>
            </span>

            {/* Desktop-only overlay caption */}
            <div className="hidden md:block absolute bottom-0 left-0 right-0 p-7 md:p-10 text-[#FDFBF7]">
              <div className="flex items-center gap-3">
                {featured.duration && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                    <Clock className="w-3 h-3" strokeWidth={1.6} />
                    {featured.duration}
                  </span>
                )}
                {featured.tag && (
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/70">
                    · {pick(featured.tag, lang)}
                  </span>
                )}
              </div>
              <h3 className="font-serif-x text-3xl md:text-5xl mt-3 leading-tight tracking-tight max-w-3xl">
                {pick(featured.title, lang)}
              </h3>
              {featured.desc && (
                <p className="mt-4 text-base md:text-lg text-[#FDFBF7]/85 leading-relaxed max-w-2xl">
                  {pick(featured.desc, lang)}
                </p>
              )}
            </div>

            {/* Brand-integrated frame: corner brackets + Xaluca monogram */}
            <VideoBrandFrame size="lg" testIdPrefix={`${testid}-featured-frame`} />
          </div>

          {/* Mobile / tablet flowing caption for the featured card */}
          <div className={`md:hidden px-5 py-6 ${cardTitleTone}`}>
            <div className="flex items-center gap-3">
              {featured.duration && (
                <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase ${isDark ? "text-[#D4A373]" : "text-[#C16542]"}`}>
                  <Clock className="w-3 h-3" strokeWidth={1.6} />
                  {featured.duration}
                </span>
              )}
              {featured.tag && (
                <span className={`text-[10px] tracking-[0.3em] uppercase ${isDark ? "text-[#FDFBF7]/70" : "text-[#5C5248]"}`}>
                  · {pick(featured.tag, lang)}
                </span>
              )}
            </div>
            <h3 className={`font-serif-x text-2xl sm:text-3xl mt-3 leading-tight tracking-tight ${cardTitleTone}`}>
              {pick(featured.title, lang)}
            </h3>
            {featured.desc && (
              <p className={`mt-3 text-sm sm:text-base leading-relaxed ${cardDescTone}`}>
                {pick(featured.desc, lang)}
              </p>
            )}
          </div>
        </button>

        {/* Secondary cards — hybrid layout:
            • Mobile / tablet: stacked. Image on top with just the Play
              button; title + full description sit in a normal-flow
              caption block below so nothing is clipped, ever.
            • Desktop (md+): the original cinematic overlay — image
              fills the whole card at aspect-[4/5] with the caption
              layered on top for editorial density. */}
        {secondary.length > 0 && (
          <div className={`mt-6 grid grid-cols-1 ${secondary.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"} gap-5`}>
            {secondary.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setActive(v)}
                className={`group relative flex flex-col md:block md:aspect-[4/5] overflow-hidden rounded-3xl text-left border ${cardBg} transition-all`}
                data-testid={`${testid}-secondary-${v.id}`}
                data-cms-scope={`${testid}.video.${v.id}`}
              >
                {/* Image wrapper — full card on desktop, top block on mobile */}
                <div className="relative w-full aspect-[4/3] md:aspect-auto md:absolute md:inset-0">
                  <img
                    src={v.poster}
                    alt={pick(v.title, lang)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" decoding="async"
                    data-cms-role="poster"
                    data-no-xaluca-logo="true"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/30 to-transparent" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#FDFBF7]/95 text-[#C16542] flex items-center justify-center group-hover:bg-[#C16542] group-hover:text-[#FDFBF7] transition-colors shadow-[0_8px_24px_rgba(0,0,0,0.4)]">
                      <Play className="w-5 h-5 md:w-6 md:h-6 ml-0.5" strokeWidth={1.5} fill="currentColor" />
                    </span>
                  </span>
                  {/* Desktop-only overlay caption (unchanged editorial look) */}
                  <div className="hidden md:block absolute bottom-0 left-0 right-0 p-6 text-[#FDFBF7]">
                    {v.duration && (
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                        {v.duration}{v.tag ? ` · ${pick(v.tag, lang)}` : ""}
                      </p>
                    )}
                    <h4 className="font-serif-x text-xl md:text-2xl mt-2 leading-tight text-[#FDFBF7]">
                      {pick(v.title, lang)}
                    </h4>
                    {v.desc && (
                      <p className="mt-2 text-[13px] leading-relaxed text-[#FDFBF7]/80 line-clamp-2">
                        {pick(v.desc, lang)}
                      </p>
                    )}
                  </div>
                </div>
                {/* Mobile / tablet flowing caption — no clamp, full text.
                    Uses the card's own background so it reads as a natural
                    continuation of the image above. */}
                <div className={`md:hidden px-5 py-5 ${cardTitleTone}`}>
                  {v.duration && (
                    <p className={`text-[10px] tracking-[0.3em] uppercase ${isDark ? "text-[#D4A373]" : "text-[#C16542]"}`}>
                      {v.duration}{v.tag ? ` · ${pick(v.tag, lang)}` : ""}
                    </p>
                  )}
                  <h4 className={`font-serif-x text-xl mt-2 leading-tight ${cardTitleTone}`}>
                    {pick(v.title, lang)}
                  </h4>
                  {v.desc && (
                    <p className={`mt-2 text-sm leading-relaxed ${cardDescTone}`}>
                      {pick(v.desc, lang)}
                    </p>
                  )}
                </div>

                {/* Brand-integrated frame on each secondary card */}
                <VideoBrandFrame size="sm" testIdPrefix={`${testid}-secondary-${v.id}-frame`} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-[#1A1513] flex items-center justify-center px-4 md:px-12"
          onClick={() => setActive(null)}
          data-testid={`${testid}-modal`}
          data-no-x-mark="true"
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            className="absolute top-6 right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
            data-testid={`${testid}-modal-close`}
            aria-label="Close"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Give the modal poster the same CMS scope + role as the
                card `<img>` above. `applyOverrides()` looks up each
                <img> by its composed slot id
                (`cms.<testid>.video.<id>.poster`) and rewrites `src`
                with the DB override — without this, the modal keeps
                the hardcoded poster from the data file while the card
                shows the admin's custom upload, so the two disagree. */}
            <div className="aspect-video bg-[#1A1513] rounded-2xl overflow-hidden relative" data-cms-scope={`${testid}.video.${active.id}`}>
              {active.embed ? (
                <iframe
                  src={active.embed}
                  title={pick(active.title, lang)}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid={`${testid}-modal-iframe`}
                />
              ) : (
                <>
                  <img src={active.poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" decoding="async" data-cms-role="poster" data-no-xaluca-logo="true"/>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                    <Play className="w-14 h-14 text-[#D4A373]" strokeWidth={1.2} fill="currentColor" />
                    <p className="font-serif-x text-3xl md:text-4xl mt-6 text-[#FDFBF7] max-w-2xl">
                      {pick(active.title, lang)}
                    </p>
                    <p className="mt-4 text-[11px] tracking-[0.3em] uppercase text-[#D4A373]">
                      {pick({
                        en: "Video coming soon · in production",
                        fr: "Vidéo bientôt disponible · en production",
                        es: "Vídeo próximamente · en producción",
                      }, lang)}
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="mt-4 text-center text-[#FDFBF7]/70">
              <h3 className="font-serif-x text-xl">{pick(active.title, lang)}</h3>
              {active.desc && (
                <p className="mt-1 text-sm max-w-2xl mx-auto text-[#FDFBF7]/60">{pick(active.desc, lang)}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoSection;
